import React from "react";
import { media } from "@glitz/core";
import { applyClassName, styled } from "@glitz/react";
import { useCart, cartItemIsOnBackorder } from "../context/CartContext";
import { GrFormTrash } from "react-icons/gr";
import { spacing, green, fontSize, fontWeight, grey, lightGrey, orange, darkGrey } from "../theme";
import SlimQuantitySelector from "./basic/SlimQuantitySelector";

const CheckoutCart: React.FC = () => {
  const { cart, removeFromCart, updateItemQuantity } = useCart();

  return (
    <Wrapper className="checkout-cart">
      <HeaderRow className="checkout-cart-header">
        <HeaderCell />
        <HeaderCell />
        <HeaderCell css={textAlignCenterDecorator}>Antal</HeaderCell>
        <HeaderCell>Á-pris</HeaderCell>
        <HeaderCell>Summa</HeaderCell>
        <HeaderCell />
      </HeaderRow>

      {cart.length > 0 ? (
        cart.map((item, index) => (
          <React.Fragment key={item.id}>
            <Row className="checkout-cart-row">
              <ImgCell className="checkout-cart-img">
                <Img src={item.img} loading="lazy" decoding="async" />
              </ImgCell>
              <NameCell className="checkout-cart-name">
                {item.name}
                {cartItemIsOnBackorder(item) && (
                  <BackorderLine>Beställningsvara – skickas när den finns i lager.</BackorderLine>
                )}
              </NameCell>
              <QuantityCell className="checkout-cart-qty">
                <SlimQuantitySelector
                  initialQuantity={item.quantity}
                  onAdd={() => updateItemQuantity(item.id, item.quantity + 1)}
                  onSubtract={() =>
                    updateItemQuantity(item.id, item.quantity - 1)
                  }
                />
              </QuantityCell>
              <UnitPriceCell className="checkout-cart-unit">{item.price} kr</UnitPriceCell>
              <SumPriceCell className="checkout-cart-sum">{item.price * item.quantity} kr</SumPriceCell>
              <ActionCell className="checkout-cart-action">
                <TrashIcon onClick={() => removeFromCart(item.id)} />
              </ActionCell>
            </Row>
            {index < cart.length - 1 && <Divider />}
          </React.Fragment>
        ))
      ) : (
        <EmptyText>Din varukorg är tom</EmptyText>
      )}
    </Wrapper>
  );
};

const GRID_COLUMNS = "70px minmax(0, 1fr) 120px 80px 80px 40px";

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  overflow: "hidden",
});

const HeaderRow = styled.div({
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  gap: spacing.m,
  alignItems: "center",
  fontWeight: fontWeight.bold,
  fontSize: fontSize.s,
  borderBottom: { style: "solid", color: grey, width: "2px" },
  paddingBottom: spacing.xs,
  ...media({ maxWidth: "768px" }, {
    display: "none",
  }),
});

const Row = styled.div({
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  gap: spacing.m,
  alignItems: "center",
  padding: { y: spacing.s },
  minWidth: 0,
  ...media({ maxWidth: "768px" }, {
    gridTemplateColumns: "56px minmax(0, 1fr) 28px",
    gridTemplateAreas: `
      "img name action"
      "img qty qty"
      "img prices prices"
    `,
    columnGap: spacing.s,
    rowGap: spacing.xs,
    alignItems: "start",
  }),
});

const HeaderCell = styled.div({
  textAlign: "right",
});

const textAlignCenterDecorator = styled({
  textAlign: "center",
});

const ImgCell = styled.div({
  width: "70px",
  height: "70px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  ...media({ maxWidth: "768px" }, {
    width: "56px",
    height: "56px",
    gridArea: "img",
    alignSelf: "center",
  }),
});

const Img = styled.img({
  maxWidth: "100%",
  maxHeight: "100%",
  width: "auto",
  height: "auto",
  objectFit: "contain",
  display: "block",
});

const NameCell = styled.div({
  fontSize: fontSize.m,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  minWidth: 0,
  overflow: "hidden",
  paddingLeft: spacing.xs,
  ...media({ maxWidth: "768px" }, {
    gridArea: "name",
    paddingLeft: 0,
    fontSize: fontSize.s,
  }),
});

const BackorderLine = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.light,
  color: darkGrey,
  borderLeft: { style: "solid", color: orange, width: "3px" },
  paddingLeft: spacing.xs,
  lineHeight: 1.35,
});

const QuantityCell = styled.div({
  display: "flex",
  justifyContent: "flex-end",
  ...media({ maxWidth: "768px" }, {
    gridArea: "qty",
    justifyContent: "flex-start",
  }),
});

const PriceCell = styled.div({
  textAlign: "right",
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  whiteSpace: "nowrap",
});

const UnitPriceCell = styled(PriceCell, {
  ...media({ maxWidth: "768px" }, {
    display: "none",
  }),
});

const SumPriceCell = styled(PriceCell, {
  ...media({ maxWidth: "768px" }, {
    gridArea: "prices",
    textAlign: "left",
    fontSize: fontSize.s,
  }),
});

const ActionCell = styled.div({
  display: "flex",
  justifyContent: "center",
  ...media({ maxWidth: "768px" }, {
    gridArea: "action",
    justifyContent: "flex-end",
  }),
});

const TrashIcon = styled(applyClassName(GrFormTrash), {
  fontSize: "25px",
  color: green,
  cursor: "pointer",
});

const Divider = styled.div({
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
});

const EmptyText = styled.div({
  padding: { y: spacing.l },
  textAlign: "center",
});

export default CheckoutCart;
